// StepUsers — Phase 5 admin user provisioning.
//
// Captures the first admin (and optionally additional staff) for the
// new tenant. The first row's role defaults to "admin"; the rest to
// "staff". On `complete()`, the backend hashes the password with the
// same bcrypt utility the auth router uses, creates a real User row
// linked to the new company_id, and the admin can log in immediately.
//
// Soft validation:
//   • Email format (HTML5 type=email + visual check)
//   • Password strength meter (≥ 8 chars with mixed-case / digit)
//   • Duplicate-email warn within the row set
import React, { useEffect, useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import {
  User, ChevronRight, Plus, Trash2, Eye, EyeOff, ShieldCheck,
  AlertTriangle, CheckCircle2,
} from 'lucide-react';

const ROLES = ['admin', 'manager', 'accountant', 'staff'];

function emptyUser(role = 'admin') {
  return { full_name: '', email: '', password: '', role };
}

function pwScore(p) {
  if (!p) return { score: 0, label: '—' };
  let s = 0;
  if (p.length >= 8)        s++;
  if (/[A-Z]/.test(p))      s++;
  if (/[a-z]/.test(p))      s++;
  if (/\d/.test(p))         s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  const labels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
  return { score: s, label: labels[s] || '—' };
}


export default function StepUsers({ initialData, onSubmit, busy }) {
  const [users, setUsers] = useState([emptyUser('admin')]);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    if (initialData?.users?.length) {
      const h = setTimeout(() => setUsers(initialData.users), 0);
      return () => clearTimeout(h);
    }
  }, [initialData]);

  const update = (idx, key, value) => {
    setUsers((arr) => arr.map((u, i) => i === idx ? { ...u, [key]: value } : u));
  };

  const add = () => setUsers((arr) => [...arr, emptyUser(arr.length === 0 ? 'admin' : 'staff')]);
  const remove = (idx) => setUsers((arr) => arr.length === 1 ? arr : arr.filter((_, i) => i !== idx));

  // Detect duplicates inside this draft (case-insensitive).
  const emails = users.map((u) => (u.email || '').trim().toLowerCase());
  const dups = new Set(emails.filter((e, i) => e && emails.indexOf(e) !== i));

  // At least one admin with valid-looking email + password ≥ 8 chars.
  const canSave = users.some(
    (u) => u.role === 'admin' && /.+@.+\..+/.test(u.email) && (u.password || '').length >= 8,
  );

  return (
    <div data-testid="step-users" className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700"><User size={18} /></div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Provision the first users</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            The first <strong>admin</strong> can log in immediately after onboarding. You can add more
            teammates later from <code className="bg-slate-100 px-1 rounded text-xs">Settings → Users</code>.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {users.map((u, idx) => {
          const pw = pwScore(u.password);
          const isDup = dups.has((u.email || '').trim().toLowerCase());
          const emailValid = !u.email || /.+@.+\..+/.test(u.email);
          return (
            <div
              key={idx}
              data-testid={`user-row-${idx}`}
              className={`p-4 rounded-lg border ${
                u.role === 'admin'
                  ? 'border-indigo-200 bg-indigo-50/30'
                  : 'border-slate-200 bg-white'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-3">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Full name</label>
                  <Input
                    data-testid={`user-name-${idx}`}
                    placeholder="Sara Al Saeed"
                    value={u.full_name}
                    onChange={(e) => update(idx, 'full_name', e.target.value)}
                  />
                </div>
                <div className="md:col-span-4">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Email</label>
                  <Input
                    data-testid={`user-email-${idx}`}
                    type="email"
                    placeholder="sara@company.com"
                    value={u.email}
                    onChange={(e) => update(idx, 'email', e.target.value)}
                    className={
                      isDup ? 'border-rose-400' :
                      !emailValid ? 'border-amber-400' : ''
                    }
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Password</label>
                  <div className="relative">
                    <Input
                      data-testid={`user-password-${idx}`}
                      type={revealed[idx] ? 'text' : 'password'}
                      value={u.password}
                      onChange={(e) => update(idx, 'password', e.target.value)}
                      placeholder="At least 8 chars"
                    />
                    <button
                      type="button"
                      data-testid={`user-pw-toggle-${idx}`}
                      onClick={() => setRevealed((r) => ({ ...r, [idx]: !r[idx] }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {revealed[idx] ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Role</label>
                  <select
                    data-testid={`user-role-${idx}`}
                    value={u.role}
                    onChange={(e) => update(idx, 'role', e.target.value)}
                    className="w-full px-2 py-2 rounded-md border border-slate-200 bg-white text-sm capitalize"
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                {users.length > 1 && (
                  <div className="md:col-span-12 flex justify-end">
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      data-testid={`user-remove-${idx}`}
                      className="text-rose-500 hover:text-rose-700 text-xs flex items-center gap-1"
                    >
                      <Trash2 size={11} /> Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Password strength + warnings line */}
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                {u.password && (
                  <div className="flex items-center gap-2" data-testid={`user-pw-strength-${idx}`}>
                    <div className="w-24 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          pw.score <= 1 ? 'bg-rose-400 w-1/5' :
                          pw.score === 2 ? 'bg-orange-400 w-2/5' :
                          pw.score === 3 ? 'bg-amber-400 w-3/5' :
                          pw.score === 4 ? 'bg-emerald-400 w-4/5' :
                          'bg-emerald-600 w-full'
                        }`}
                      />
                    </div>
                    <span className={`text-[11px] font-medium ${
                      pw.score <= 1 ? 'text-rose-600' :
                      pw.score === 2 ? 'text-orange-600' :
                      pw.score === 3 ? 'text-amber-700' :
                      'text-emerald-700'
                    }`}>{pw.label}</span>
                  </div>
                )}
                {isDup && (
                  <span className="text-rose-600 flex items-center gap-1">
                    <AlertTriangle size={11} /> Email duplicated in this list
                  </span>
                )}
                {!emailValid && u.email && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <AlertTriangle size={11} /> Email looks malformed
                  </span>
                )}
                {u.role === 'admin' && (
                  <span className="text-indigo-700 flex items-center gap-1 ml-auto">
                    <ShieldCheck size={11} /> Owns the tenant
                  </span>
                )}
              </div>
            </div>
          );
        })}
        <Button variant="outline" onClick={add} data-testid="user-add-btn">
          <Plus size={14} className="mr-1.5" /> Add another user
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
        <ShieldCheck size={14} className="flex-shrink-0 mt-0.5" />
        <div>
          Passwords are hashed with bcrypt before they hit the database — the wizard never logs the plaintext.
          The new admin will be able to sign in at <code className="bg-white/70 px-1 rounded">/login</code> right after completion.
        </div>
      </div>

      <div className="flex items-center justify-between">
        {!canSave && (
          <span className="text-xs text-amber-700 flex items-center gap-1">
            <AlertTriangle size={11} /> Provide at least one admin with a valid email and 8+ char password.
          </span>
        )}
        {canSave && (
          <span className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 size={11} /> Ready to provision {users.length} user{users.length !== 1 ? 's' : ''}.
          </span>
        )}
        <Button
          onClick={() => onSubmit({ users })}
          disabled={!canSave || busy}
          data-testid="users-save-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          Save & continue <ChevronRight size={14} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
